import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setOperationMode, OperationMode } from '../../store/slices/settingsSlice';
import { useDevice } from '../../context/DeviceContext';

// ─── mode definitions ─────────────────────────────────────────────────────────
const MODES: {
  id: OperationMode;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  border: string;
}[] = [
  {
    id: 'auto',
    title: 'Automático',
    description: 'Detecta el tipo de dispositivo. Tablets y móviles usan el modo paso a paso; computadoras usan el formulario clásico.',
    color:  '#3b82f6',
    bg:     '#eff6ff',
    border: '#93c5fd',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
      </svg>
    ),
  },
  {
    id: 'steps',
    title: 'Modo Táctil (pasos)',
    description: 'Siempre usa el flujo por pasos con teclado táctil en pantalla. Ideal para tablets y terminales de punto de venta.',
    color:  '#8b5cf6',
    bg:     '#f5f3ff',
    border: '#c4b5fd',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
      </svg>
    ),
  },
  {
    id: 'normal',
    title: 'Modo PC (formulario)',
    description: 'Siempre usa el formulario clásico con teclado físico del sistema. Ideal para computadoras de escritorio y laptops.',
    color:  '#10b981',
    bg:     '#ecfdf5',
    border: '#6ee7b7',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
      </svg>
    ),
  },
];

// ─── component ────────────────────────────────────────────────────────────────
const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { operationMode } = useAppSelector((state) => (state as any).settings);
  const { deviceType } = useDevice();

  const deviceLabel: Record<string, string> = {
    mobile:  'Teléfono móvil',
    tablet:  'Tablet / Terminal POS',
    desktop: 'Computadora / Laptop',
  };

  const deviceModeLabel: Record<string, string> = {
    mobile:  'paso a paso (táctil)',
    tablet:  'paso a paso (táctil)',
    desktop: 'formulario clásico (PC)',
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Configuración
        </h1>
        <p className="text-slate-500 text-sm mt-1">Preferencias del sistema PayPlus POS.</p>
      </div>

      {/* Dispositivo detectado */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V9l-6-6z"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 3v6h6"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-700">Dispositivo detectado</p>
          <p className="text-xs text-slate-500">{deviceLabel[deviceType] || deviceType} — usaría <strong>{deviceModeLabel[deviceType]}</strong> en modo Automático</p>
        </div>
      </div>

      {/* Modo de operaciones */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4">
        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Modo de operaciones</p>
        <p className="text-xs text-slate-400 mb-5">
          Controla cómo se muestran las pantallas de recargas, paquetes, facturas y seguros.
        </p>

        <div className="space-y-3">
          {MODES.map(mode => {
            const active = operationMode === mode.id;
            return (
              <button key={mode.id} type="button" onClick={() => dispatch(setOperationMode(mode.id))}
                className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all duration-150"
                style={{
                  borderColor: active ? mode.color : '#e2e8f0',
                  background:  active ? mode.bg    : '#fff',
                  boxShadow:   active ? `0 0 0 1px ${mode.color}30` : 'none',
                }}>
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: active ? mode.color : '#f1f5f9', color: active ? '#fff' : '#94a3b8' }}>
                  {mode.icon}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800">{mode.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{mode.description}</p>
                </div>

                {/* Check */}
                <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all"
                  style={{ borderColor: active ? mode.color : '#cbd5e1', background: active ? mode.color : 'transparent' }}>
                  {active && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Info */}
      <p className="text-xs text-center text-slate-400 mt-2">
        La preferencia se guarda localmente y aplica de inmediato.
      </p>
    </div>
  );
};

export default SettingsPage;
